import { CartItem, i18nProps } from '~Types';
import { Item } from '@baemingo/table-events/dist/types';
import { Item as RefundItem } from '~Components/Refunds/Provider/types';
import {
  GetCompetitions_Venue_competitions,
  GetCompetitions_Venue_competitions_score,
} from './__queries__';
import { COMPETITION_TYPE } from '../../../__queries__/globalTypes';
import { DateTime } from 'luxon';
import { Staffs } from '~reducers';

export const sortParticipantsBasedOnCompType = (
  participants: GetCompetitions_Venue_competitions_score[],
  competitionType: COMPETITION_TYPE,
  hasOnlySpecificSales: boolean,
) => {
  return [...participants].sort((a, b) => {
    switch (competitionType) {
      case COMPETITION_TYPE.SALES_AMOUNT: {
        return b.sales.amount - a.sales.amount;
      }
      case COMPETITION_TYPE.SALES_AVERAGE: {
        const avgASales = a.sales.orderCount ? a.sales.amount / a.sales.orderCount : 0;
        const avgBSales = b.sales.orderCount ? b.sales.amount / b.sales.orderCount : 0;
        return avgBSales - avgASales;
      }
      case COMPETITION_TYPE.SALES_COUNT: {
        if (hasOnlySpecificSales) {
          return b.sales.itemCount - a.sales.itemCount;
        } else {
          return b.sales.orderCount - a.sales.orderCount;
        }
      }
      case COMPETITION_TYPE.TIPS_AMOUNT: {
        return b.tips.amount - a.tips.amount;
      }
      case COMPETITION_TYPE.TIPS_AVERAGE: {
        const avgATips = a.tips.count ? a.tips.amount / a.tips.count : 0;
        const avgBTips = b.tips.count ? b.tips.amount / b.tips.count : 0;
        return avgBTips - avgATips;
      }
      case COMPETITION_TYPE.TIPS_COUNT: {
        return b.tips.count - a.tips.count;
      }
      default:
        return 0; // In case of an unknown competition type
    }
  });
};

export const getCorrectValueBasedOnCompType = (
  participant: GetCompetitions_Venue_competitions_score,
  competitionType: COMPETITION_TYPE,
  hasOnlySpecificSales: boolean,
  formatCurrency: (value: number) => string,
  i18n: i18nProps,
) => {
  switch (competitionType) {
    case COMPETITION_TYPE.SALES_AMOUNT:
      return formatCurrency(participant.sales.amount);
    case COMPETITION_TYPE.SALES_AVERAGE:
      return formatCurrency(
        participant.sales.orderCount ? participant.sales.amount / participant.sales.orderCount : 0,
      );
    case COMPETITION_TYPE.SALES_COUNT:
      if (hasOnlySpecificSales) {
        return participant.sales.itemCount + ' ' + i18n('Competitions.products.count');
      } else {
        return participant.sales.orderCount + ' ' + i18n('Competitions.sales.count');
      }
    case COMPETITION_TYPE.TIPS_AMOUNT:
      return formatCurrency(participant.tips.amount);
    case COMPETITION_TYPE.TIPS_AVERAGE:
      return formatCurrency(
        participant.tips.count ? participant.tips.amount / participant.tips.count : 0,
      );
    case COMPETITION_TYPE.TIPS_COUNT:
      return participant.tips.count + ' ' + i18n('Competitions.tips.count');
    default:
      return '0'; // In case of an unknown competition type
  }
};

export const getDataToUpdate = (
  competition: GetCompetitions_Venue_competitions,
  items: CartItem[] | Item[] | RefundItem[],
) => {
  if (
    competition.onlySpecificSales.items.length === 0 &&
    competition.onlySpecificSales.categories.length === 0
  ) {
    // Map items to a common structure
    const mappedItems = items.map((item) => ({
      amount: item.amount,
      discount: item.discount,
    }));

    const amount = mappedItems.reduce((acc, item) => {
      return acc + item.amount - item.discount;
    }, 0);

    return {
      amount: amount,
      itemCount: items.length,
    };
  }

  // It is possible to have both categories and products in the same competition
  // so we need to check both
  // calculate the item amounts based on if the item is in the categories or products list. If it is in both it should only be added once.
  let amount = 0;
  let itemCount = 0;
  const categories = competition.onlySpecificSales.categories || [];
  const specificItems = competition.onlySpecificSales.items || [];

  items.forEach((item) => {
    if (categories.includes(item.categoryId) || specificItems.includes(item.itemId)) {
      itemCount++;
      amount += item.amount - item.discount;
    }
  });

  return { amount, itemCount };
};

// To check if the competition has started, is ongoing or has ended
export const getCompetitionDateStatus = (startDate: string, endDate: string) => {
  const now = DateTime.now();
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);

  const isUpcoming = now < start;
  const isOngoing = now >= start && now <= end;
  const isCompleted = now > end;

  return { isUpcoming, isOngoing, isCompleted };
};

export const getStaffName = (staffId: string, staffs: Staffs, i18n: i18nProps) => {
  return staffs?.find((staff) => staff.id === staffId)?.name ?? i18n('Competitions.Unknown');
};

export const formatPosition = (position: number, i18n: i18nProps) => {
  const positionMap: { [key: number]: string } = {
    1: i18n('Competitions.1st'),
    2: i18n('Competitions.2nd'),
    3: i18n('Competitions.3rd'),
  };

  if (position in positionMap) {
    return `${positionMap[position]} ${i18n('Competitions.Place')}`;
  }

  if (position > 99) {
    return `${position}. ${i18n('Competitions.Place')}`;
  }

  return `${position}${i18n('Competitions.th')} ${i18n('Competitions.Place')}`;
};

export const isStaffParticipant = (
  competition: GetCompetitions_Venue_competitions,
  staffId: string,
): boolean => {
  // If the competition.participants array is empty all staff should be participants
  const staffIsAParticipant =
    competition.participants?.length === 0
      ? true
      : competition.participants?.find((participant) => participant === staffId);

  return staffIsAParticipant ? true : false;
};

// Utility function to check if refetch is needed
export const shouldRefetchCompetitions = (
  competitions: GetCompetitions_Venue_competitions[],
  upcomingCompetitions: GetCompetitions_Venue_competitions[],
  now: DateTime,
): boolean => {
  let shouldRefetch = false;

  // Check if any ongoing competition has finished
  competitions.forEach((competition) => {
    if (DateTime.fromISO(competition.endDate) < now) {
      shouldRefetch = true;
    }
  });

  // Check if any upcoming competition has started
  upcomingCompetitions.forEach((competition) => {
    if (DateTime.fromISO(competition.startDate) <= now) {
      shouldRefetch = true;
    }
  });

  return shouldRefetch;
};

// Utility function to calculate the next event time
export const calculateNextEventTime = (
  competitions: GetCompetitions_Venue_competitions[],
  now: DateTime,
): DateTime | null => {
  return competitions.reduce<DateTime | null>((next, competition) => {
    const startDate = DateTime.fromISO(competition.startDate);
    const endDate = DateTime.fromISO(competition.endDate);

    // Find the closest future event time
    if (startDate > now && (!next || startDate < next)) {
      return startDate;
    }
    if (endDate > now && (!next || endDate < next)) {
      return endDate;
    }
    return next;
  }, null);
};
